import { useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Stack from "@mui/material/Stack";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import SystemUpdateAltOutlinedIcon from "@mui/icons-material/SystemUpdateAltOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { alpha, useTheme } from "@mui/material/styles";
import PropTypes from "prop-types";
import MDButton from "components/MDButton";

const SESSION_KEY = "desktopSoftwareUpdateModalDismissed";

/** True when using Flask `/api/descarga-actualizacion-desktop` (fetch works same-origin). */
export function isDesktopDownloadViaApi() {
  const flag = String(process.env.REACT_APP_DESKTOP_DOWNLOAD_VIA_API || "")
    .trim()
    .toLowerCase();
  return flag === "1" || flag === "true" || flag === "yes";
}

/**
 * API proxy on REACT_APP_SERVIDOR, or direct REACT_APP_DESKTOP_APP_DOWNLOAD_URL (e.g. S3).
 */
export function resolveDesktopDownloadUrl() {
  const servidor = String(process.env.REACT_APP_SERVIDOR || "").replace(/\/$/, "");
  if (isDesktopDownloadViaApi() && servidor) {
    return `${servidor}/api/descarga-actualizacion-desktop`;
  }
  return String(process.env.REACT_APP_DESKTOP_APP_DOWNLOAD_URL+localStorage.getItem("nombrenegocio").replaceAll(" ","").toLowerCase()+"/mysetup.exe" || "").trim();
}

/** Last path segment of a URL, for suggested download filename. */
function fileNameFromUrl(url) {
  try {
    const u = new URL(url, typeof window !== "undefined" ? window.location.href : undefined);
    const raw = u.pathname.split("/").filter(Boolean).pop() || "";
    const base = decodeURIComponent(raw.split("?")[0] || "").trim();
    if (base) return base;
  } catch {
    /* ignore */
  }
  return "actualizacion";
}

/**
 * Fetch + blob save. Use only for same-origin URLs (e.g. API proxy). S3 direct URLs will fail CORS.
 */
export async function downloadFileFromUrl(url) {
  const trimmed = String(url || "").trim();
  if (!trimmed) return;

  const envName = String(process.env.REACT_APP_DESKTOP_APP_FILENAME || "").trim();
  const name = envName || fileNameFromUrl(trimmed);

  const credRaw = String(process.env.REACT_APP_DESKTOP_DOWNLOAD_CREDENTIALS || "")
    .trim()
    .toLowerCase();
  let credentials = "same-origin";
  if (credRaw === "omit") credentials = "omit";
  else if (credRaw === "include") credentials = "include";

  const response = await fetch(trimmed, {
    method: "GET",
    mode: "cors",
    credentials,
    cache: "no-store",
  });

  if (!response.ok) {
    throw new Error(`Descarga fallida (${response.status})`);
  }

  const blob = await response.blob();
  const blobUrl = URL.createObjectURL(blob);

  try {
    const a = document.createElement("a");
    a.href = blobUrl;
    a.setAttribute("download", name);
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  } finally {
    setTimeout(() => URL.revokeObjectURL(blobUrl), 30_000);
  }
}

/** Open HTTPS URL in a new tab (S3 / cross-origin). Must run synchronously inside the click handler. */
function openDownloadInNewTab(url) {
  const w = window.open(url, "_blank", "noopener,noreferrer");
  if (!w) {
    const a = document.createElement("a");
    a.href = url;
    a.target = "_blank";
    a.rel = "noopener noreferrer";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }
}

async function fileExists(url) {
  try {
    const response = await fetch(url, {
      method: 'HEAD', // lightweight request
    });
    
    return response.status == 200; // true if status is 200–299
  } catch (error) {
    return false;
  }
}


export async function shouldShowDesktopUpdateModal() {
  const status = await fileExists(resolveDesktopDownloadUrl());
  return status;

}

export function dismissDesktopUpdateModalForSession() {
  if (typeof window === "undefined") return;
  sessionStorage.setItem(SESSION_KEY, "1");
}

/**
 * REACT_APP_SHOW_DESKTOP_UPDATE_MODAL, REACT_APP_DESKTOP_APP_DOWNLOAD_URL,
 * REACT_APP_DESKTOP_DOWNLOAD_VIA_API (use Flask proxy), REACT_APP_DESKTOP_APP_FILENAME.
 */
function DesktopSoftwareUpdateModal({ open, onClose }) {
  const theme = useTheme();
  const downloadUrl = useMemo(() => resolveDesktopDownloadUrl(), []);
  

  const handleClose = () => {
    dismissDesktopUpdateModalForSession();
    onClose();
  };

  const handleDownload = () => {
    const url = resolveDesktopDownloadUrl();
    if (!url) {
      handleClose();
      return;
    }

    if (!isDesktopDownloadViaApi()) {
      openDownloadInNewTab(url);
      handleClose();
      return;
    }

    const runFetchDownload = async () => {
      try {
        await downloadFileFromUrl(url);
      } catch (err) {
        let msg = "No se pudo descargar.";
        if (err && typeof err.message === "string") {
          if (err.message === "Failed to fetch" || err.name === "TypeError") {
            msg =
              "No se pudo conectar. Pruebe REACT_APP_DESKTOP_DOWNLOAD_VIA_API=false y URL directa de S3, o configure el proxy en el servidor.";
          } else {
            msg = err.message;
          }
        }
        // eslint-disable-next-line no-alert -- fallo de red / CORS
        window.alert(msg);
      } finally {
        handleClose();
      }
    };
    runFetchDownload();
  };

  return (
    <Dialog
      open={open}
      onClose={(_, reason) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
          handleClose();
        }
      }}
      maxWidth={false}
      fullWidth
      PaperProps={{
        sx: {
          width: { xs: "100%", sm: "min(96vw, 720px)" },
          maxHeight: { xs: "100%", sm: "min(92vh, 640px)" },
          m: { xs: 0, sm: 2 },
          borderRadius: { xs: 0, sm: 3 },
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          bgcolor: theme.palette.background.default,
          backgroundImage: "none",
          boxShadow: 24,
        },
      }}
    >
      <Box
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: { xs: 2, sm: 2.5 },
          display: "flex",
          alignItems: "flex-start",
          justifyContent: "space-between",
          gap: 2,
          background: "linear-gradient(135deg, #0d47a1 0%, #1565c0 38%, #1976d2 72%, #2196f3 100%)",
          color: theme.palette.common.white,
          boxShadow: 2,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center">
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              bgcolor: alpha("#fff", 0.15),
            }}
          >
            <SystemUpdateAltOutlinedIcon sx={{ fontSize: 32, color: theme.palette.common.white }} />
          </Box>
          <Box>
            <Typography
              variant="h4"
              fontWeight={700}
              letterSpacing={-0.5}
              sx={(t) => ({ color: t.palette.common.white })}
            >
              Actualice el software de escritorio
            </Typography>
            <Typography
              variant="body1"
              sx={{ mt: 0.5, color: alpha(theme.palette.common.white, 0.92), maxWidth: 520 }}
            >
              Hay una nueva versión disponible. Actualizar mejora la sincronización y la
              estabilidad.
            </Typography>
          </Box>
        </Stack>
        <IconButton
          aria-label="cerrar"
          onClick={handleClose}
          sx={{
            color: "inherit",
            bgcolor: alpha("#fff", 0.1),
            "&:hover": { bgcolor: alpha("#fff", 0.2) },
          }}
        >
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent
        sx={{
          flex: 1,
          pt: 3,
          px: { xs: 2, sm: 3 },
          pb: 2,
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Typography variant="body1" color="text">
          Para continuar con el mejor rendimiento, descargue e instale la última versión de la
          aplicación de escritorio en su PC. Si ya la instaló, abra la app y permita que complete la
          actualización.
        </Typography>
        <Box
          sx={{
            mt: 1,
            p: 2,
            borderRadius: 2,
            bgcolor: alpha(theme.palette.info.main, 0.08),
            border: "1px solid",
            borderColor: alpha(theme.palette.info.main, 0.25),
          }}
        >
          <Typography variant="subtitle2" fontWeight={600} color="info.dark" gutterBottom>
            Recomendado
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Cierre la aplicación de escritorio antes de instalar. Tras actualizar, vuelva a iniciar
            sesión si se le solicita.
          </Typography>
        </Box>
      </DialogContent>

      <DialogActions
        sx={{
          flexShrink: 0,
          px: { xs: 2, sm: 3 },
          py: 2,
          gap: 1.5,
          flexWrap: "wrap",
          justifyContent: { xs: "stretch", sm: "flex-end" },
          borderTop: "1px solid",
          borderColor: "divider",
        }}
      >
        <MDButton variant="outlined" color="dark" onClick={handleClose} sx={{ minWidth: 120 }}>
          Más tarde
        </MDButton>
        {downloadUrl ? (
          <MDButton
            variant="gradient"
            color="info"
            onClick={handleDownload}
            startIcon={<DownloadOutlinedIcon />}
            sx={{ minWidth: 200 }}
          >
            Descargar actualización
          </MDButton>
        ) : (
          <MDButton variant="gradient" color="info" onClick={handleClose} sx={{ minWidth: 160 }}>
            Entendido
          </MDButton>
        )}
      </DialogActions>
    </Dialog>
  );
}

DesktopSoftwareUpdateModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DesktopSoftwareUpdateModal;
